import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useNoIndex } from '../lib/seoHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  useNoIndex();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: ''
  });
  const [error, setError] = useState(null);

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('sdc_admin_token');
    if (token) {
      // Verify token is still valid
      axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(() => navigate('/admin/dashboard'))
      .catch(() => localStorage.removeItem('sdc_admin_token'));
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isLogin) {
        // Login
        const response = await axios.post(`${API}/admin/login`, {
          username: formData.username,
          password: formData.password
        });
        
        localStorage.setItem('sdc_admin_token', response.data.access_token);
        localStorage.setItem('sdc_admin_user', JSON.stringify({
          username: response.data.username,
          role: response.data.role
        }));
        
        toast.success(`Welcome back, ${response.data.username}!`);
        navigate('/admin/dashboard');
      } else {
        // Register
        if (formData.password !== formData.confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }
        
        if (formData.password.length < 8) {
          setError('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        
        await axios.post(`${API}/admin/register`, {
          username: formData.username,
          password: formData.password,
          email: formData.email || null
        });
        
        toast.success('Account created! Please log in.');
        setIsLogin(true);
        setFormData({ ...formData, password: '', confirmPassword: '' });
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'An error occurred';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#d9fb06] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#1a1c1b]" />
          </div>
          <h1 className="text-2xl font-black text-[#d9fb06] uppercase">
            Admin Portal
          </h1>
          <p className="text-[#888680] text-sm mt-2">
            Social Dispute Co. Case Management
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-2xl p-8">
          <div className="flex mb-6">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 text-center text-sm font-medium transition-colors ${
                isLogin 
                  ? 'text-[#d9fb06] border-b-2 border-[#d9fb06]' 
                  : 'text-[#888680] border-b border-[#3f4816]'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 text-center text-sm font-medium transition-colors ${
                !isLogin 
                  ? 'text-[#d9fb06] border-b-2 border-[#d9fb06]' 
                  : 'text-[#888680] border-b border-[#3f4816]'
              }`}
            >
              Register
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-6 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-[#dfddd6]">Username</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888680]" />
                <Input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] focus:border-[#d9fb06]"
                  placeholder="Enter username"
                  data-testid="admin-username"
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="email" className="text-[#dfddd6]">Email (optional)</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] focus:border-[#d9fb06]"
                  placeholder="admin@socialdisputeco.com"
                />
              </div>
            )}

            <div>
              <Label htmlFor="password" className="text-[#dfddd6]">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888680]" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] focus:border-[#d9fb06]"
                  placeholder="Enter password"
                  data-testid="admin-password"
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="confirmPassword" className="text-[#dfddd6]">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required={!isLogin}
                  className="mt-1 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] focus:border-[#d9fb06]"
                  placeholder="Confirm password"
                />
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold py-3"
              data-testid="admin-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-[#888680] text-xs mt-6">
          Protected area. Authorized personnel only.
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
